/**
 * My Bag — the Gap Map's layout model (MY_BAG_PLAN.md §4, §3.5).
 *
 * Pure. No React, no network. It turns the ladder plus one `BagAnalysis` into
 * the rungs, gaps and scale the two orientations both draw from, so mobile and
 * desktop cannot drift apart on what a gap is or what colour it should be.
 *
 * **This file invents no threshold.** A gap's tone comes from whether the
 * server put that pair in `analysis.holes` or `analysis.overlaps` — it is never
 * recomputed here. §3.5's tolerance is provenance-scaled and lives in
 * `bag_gapping.tol_rung`; a second copy of `2 / 4 / 8` on this side of the wire
 * is exactly the drift the plan spends §3.5 avoiding. It is also why the
 * after-state is fetched with a second `/bag/analyze` call rather than
 * re-coloured locally.
 */
import {
  BagAnalysis,
  BagSlot,
  ClubCategory,
  DistanceConfidence,
  DistanceProvenance,
} from '../../types/bag.types';

export type GapTone = 'healthy' | 'overlap' | 'hole';

export interface MapRung {
  slotOrder: number;
  label: string;
  category: ClubCategory;
  /** The carry being drawn — the projected one when Apply is on. */
  carry: number;
  /** What the golfer actually gave us, so the delta can be shown. */
  carryBefore: number;
  provenance: DistanceProvenance;
  confidence?: DistanceConfidence | null;
  benched: boolean;
  /** Non-zero only when a bend moved this rung. */
  bendDelta: number;
  /** A swap finding names this slot. The rung does not move — see §6.7. */
  swapProposed: boolean;
}

export interface MapGap {
  upperLabel: string;
  lowerLabel: string;
  yards: number;
  tone: GapTone;
  /** The server's provenance-scaled tolerance, when this pair fired. */
  tolYards: number | null;
  closedByBend: boolean;
}

export interface GapMapModel {
  /** In-bag, carrying rungs, long -> short. The gap chain runs down this. */
  rungs: MapRung[];
  /** Benched clubs — drawn ghosted, never gapped (§4). */
  ghosts: MapRung[];
  /** `rungs.length - 1` entries, aligned to the space below each rung. */
  gaps: MapGap[];
  /** Inside the 14, holds no distance, never gaps (§4). */
  putterLabels: string[];
  /** In-bag clubs with no carry yet — the map draws around them. */
  missingLabels: string[];
  /** Scale domain, padded, covering before *and* after so Apply animates. */
  domainLow: number;
  domainHigh: number;
  coverLow: number | null;
  coverHigh: number | null;
}

const gapKey = (upper: string, lower: string) => `${upper} > ${lower}`;

const byCarryDesc = (a: MapRung, b: MapRung) => b.carry - a.carry;

export interface BuildOptions {
  /** `analysis.projected_carries`, applied when the Apply toggle is on. */
  projected?: Record<string, number> | null;
  /** Slot labels a swap finding names. */
  swapLabels?: Set<string>;
}

export function buildGapMapModel(
  slots: BagSlot[],
  analysis: BagAnalysis | null,
  options: BuildOptions = {}
): GapMapModel {
  const projected = options.projected ?? null;
  const swapLabels = options.swapLabels ?? new Set<string>();

  const putterLabels: string[] = [];
  const missingLabels: string[] = [];
  const rungs: MapRung[] = [];
  const ghosts: MapRung[] = [];

  for (const s of slots) {
    if (s.club_category === 'putter') {
      if (s.in_bag) putterLabels.push(s.club_label);
      continue;
    }
    if (s.carry_yards === null || s.carry_yards === undefined) {
      if (s.in_bag) missingLabels.push(s.club_label);
      continue;
    }
    const before = s.carry_yards;
    // A benched club is off the ladder, so no bend was ever planned for it —
    // `projected_carries` only ever holds gapping rungs.
    const after = (projected && projected[s.club_label]) ?? before;
    const rung: MapRung = {
      slotOrder: s.slot_order,
      label: s.club_label,
      category: s.club_category,
      carry: s.in_bag ? after : before,
      carryBefore: before,
      provenance: s.distance_provenance,
      confidence: s.distance_confidence ?? null,
      benched: !s.in_bag,
      bendDelta: s.in_bag ? Math.round((after - before) * 10) / 10 : 0,
      swapProposed: swapLabels.has(s.club_label),
    };
    (s.in_bag ? rungs : ghosts).push(rung);
  }

  rungs.sort(byCarryDesc);
  ghosts.sort(byCarryDesc);

  const flagged = new Map<string, MapGap>();
  for (const h of analysis?.holes ?? []) {
    flagged.set(gapKey(h.upper_label, h.lower_label), {
      upperLabel: h.upper_label,
      lowerLabel: h.lower_label,
      yards: h.gap_yards,
      tone: 'hole',
      tolYards: h.tol_yards,
      closedByBend: h.closed_by_bend,
    });
  }
  for (const o of analysis?.overlaps ?? []) {
    flagged.set(gapKey(o.upper_label, o.lower_label), {
      upperLabel: o.upper_label,
      lowerLabel: o.lower_label,
      yards: o.gap_yards,
      tone: 'overlap',
      tolYards: o.tol_yards,
      closedByBend: false,
    });
  }

  const gaps: MapGap[] = [];
  for (let i = 0; i + 1 < rungs.length; i += 1) {
    const upper = rungs[i];
    const lower = rungs[i + 1];
    const drawn = Math.round((upper.carry - lower.carry) * 10) / 10;
    const hit = flagged.get(gapKey(upper.label, lower.label));
    gaps.push(
      hit
        ? { ...hit, yards: drawn }
        : {
            upperLabel: upper.label,
            lowerLabel: lower.label,
            yards: drawn,
            tone: 'healthy',
            tolYards: null,
            closedByBend: false,
          }
    );
  }

  // The domain spans both states so a rung moving under Apply reads as the rung
  // moving, not as the whole chart rescaling underneath it.
  const carries: number[] = [];
  for (const r of rungs.concat(ghosts)) carries.push(r.carry, r.carryBefore);

  let domainLow = 0;
  let domainHigh = 1;
  if (carries.length > 0) {
    const lo = Math.min.apply(null, carries);
    const hi = Math.max.apply(null, carries);
    // A truncated baseline is what gives 13 rungs usable resolution on a phone.
    // Differences are preserved exactly under it, and the axis labels say where
    // it starts, so nothing is being hidden.
    const pad = Math.max(10, (hi - lo) * 0.08);
    domainLow = Math.floor(lo - pad);
    domainHigh = Math.ceil(hi + pad);
  }

  return {
    rungs,
    ghosts,
    gaps,
    putterLabels,
    missingLabels,
    domainLow,
    domainHigh,
    coverLow: rungs.length > 0 ? rungs[rungs.length - 1].carry : null,
    coverHigh: rungs.length > 0 ? rungs[0].carry : null,
  };
}

/**
 * Round a yardage the way the backend prints one.
 *
 * Python's `f"{x:.0f}"` rounds half to **even**; JS `Math.round` rounds half
 * **up**. The server's finding summaries are formatted the first way, so a
 * 24.5-yard gap prints "24-yard hole between your 3w and 4i" in the sentence
 * and would print "25y" in the chip beside it — one card contradicting itself.
 * Every yardage this UI rounds goes through here so that cannot happen.
 */
export function roundYards(n: number): number {
  const floor = Math.floor(n);
  if (Math.abs(n - floor - 0.5) > 1e-9) return Math.round(n);
  return floor % 2 === 0 ? floor : floor + 1;
}

/** Where a carry sits on the scale, 0 at `domainLow` and 1 at `domainHigh`. */
export function fractionOf(model: GapMapModel, carry: number): number {
  const span = model.domainHigh - model.domainLow;
  if (span <= 0) return 0;
  return Math.min(1, Math.max(0, (carry - model.domainLow) / span));
}

/** Round yardages inside the domain, for the gridlines. */
export function axisTicks(model: GapMapModel, count = 4): number[] {
  const span = model.domainHigh - model.domainLow;
  if (span <= 0) return [];
  const step = Math.max(10, Math.round(span / (count + 1) / 10) * 10);
  const out: number[] = [];
  for (let v = Math.ceil(model.domainLow / step) * step; v < model.domainHigh; v += step) {
    out.push(v);
  }
  return out;
}

const COUNT_WORDS = [
  'no',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
];

export function countWord(n: number): string {
  return n >= 0 && n < COUNT_WORDS.length ? COUNT_WORDS[n] : String(n);
}

/** §4's one-line verdict — "You cover 105-265y with two holes and one overlap." */
export function verdictSentence(model: GapMapModel, analysis: BagAnalysis | null): string {
  if (model.rungs.length === 0) {
    return 'Nothing in the bag carries a distance yet, so there is no ladder to draw.';
  }
  if (model.rungs.length === 1) {
    return `One club on the ladder, at ${roundYards(model.rungs[0].carry)}y — a gap needs two.`;
  }
  const lo = roundYards(model.coverLow ?? 0);
  const hi = roundYards(model.coverHigh ?? 0);
  const holes = (analysis?.holes ?? []).length;
  const overlaps = (analysis?.overlaps ?? []).length;

  if (holes === 0 && overlaps === 0) {
    return `You cover ${lo}–${hi}y with no holes and no overlaps.`;
  }
  const parts: string[] = [];
  if (holes > 0) parts.push(`${countWord(holes)} hole${holes === 1 ? '' : 's'}`);
  if (overlaps > 0) parts.push(`${countWord(overlaps)} overlap${overlaps === 1 ? '' : 's'}`);
  return `You cover ${lo}–${hi}y with ${parts.join(' and ')}.`;
}

/**
 * The ladder with the bend plan applied, for the second `/bag/analyze` call
 * behind the Apply toggle.
 *
 * Provenance is carried through unchanged: a projected carry is still the
 * golfer's own confirmed number with a bend added to it, and re-labelling it
 * `estimated` would widen the tolerance the after-state is judged on and make
 * the toggle look like it fixed more than it did.
 */
export function slotsWithBendsApplied(
  slots: BagSlot[],
  projected: Record<string, number> | null | undefined
): BagSlot[] {
  if (!projected) return slots;
  return slots.map((s) => {
    if (!s.in_bag) return s;
    const next = projected[s.club_label];
    if (next === undefined || s.carry_yards === null || s.carry_yards === undefined) return s;
    if (next === s.carry_yards) return s;
    return { ...s, carry_yards: next };
  });
}
